import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute } from '@tanstack/react-router'

import { AdminSidebarContainer } from '@/components/app/admin-sidebar-container'
import { ErrorPanel } from '@/components/app/error-panel'
import { AdminTenantsWorkspace } from '@/components/screens/admin-tenants-workspace'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'

export const Route = createFileRoute('/admin/tenants/')({
  component: Page,
})

function Page() {
  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebarContainer />
      <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 xl:px-10">
        <TenantsContainer />
      </main>
    </div>
  )
}

function TenantsContainer() {
  const client = api.useClient()
  const tenantsQuery = api.useFetchAdminTenantsQuery()
  const plansQuery = api.useFetchAdminPlansQuery()

  // 一覧・プランのどちらも初回取得できていなければエラー扱いにする。
  // 一度表示できた後の refetch 失敗ではエラーパネルに差し替えない（users.tsx と同じ考え方）。
  // 未取得だがエラーでもない（読み込み中）場合は、画面ごと差し替えずに DataTable の skeleton に任せる
  // （画面まるごとの CenteredLoading をやめる。2026/08 レビューFB対応）
  if ((isNull(tenantsQuery.data) || isNull(plansQuery.data)) && (isSome(tenantsQuery.error) || isSome(plansQuery.error))) {
    return <ErrorPanel title="テナント一覧の取得に失敗しました" description="しばらくしてから再度お試しください。" />
  }

  // mutation 本体と一覧の再取得は try を分ける（users.tsx と同じ理由。
  // refetch 失敗を「操作の失敗」として報告しない）
  const runMutation = async (
    mutate: () => Promise<unknown>,
    failureMessage: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await mutate()
    } catch (error) {
      return { ok: false, message: userFacingErrorMessage(error, failureMessage) }
    }
    try {
      await tenantsQuery.refetch()
    } catch {
      // 再取得の失敗を「操作の失敗」として報告しない
    }
    return { ok: true }
  }

  return (
    <AdminTenantsWorkspace
      tenants={tenantsQuery.data?.adminTenants.tenants ?? []}
      total={tenantsQuery.data?.adminTenants.total ?? 0}
      plans={plansQuery.data?.adminPlans ?? []}
      loading={tenantsQuery.loading}
      onCreate={async (input) =>
        await runMutation(async () => await client.createTenant({ tenant: input }), 'テナントの作成に失敗しました')
      }
      onUpdateTenant={async (input) =>
        await runMutation(async () => await client.updateTenant({ tenant: input }), 'テナントの更新に失敗しました')
      }
    />
  )
}
